import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faArrowCircleLeft,
  faArrowCircleRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { isType } from '@sorare/core/src/lib/gql';

import type { CardTransaction_tokenPrice } from './__generated__/index.graphql';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, max-content) minmax(0, max-content);
  column-gap: var(--unit);
  row-gap: var(--half-unit);
`;

const Nickname = styled(LabelM)`
  ${ellipsis}
`;

type Props = {
  tokenPrice: CardTransaction_tokenPrice;
};

export const CardTransaction = ({ tokenPrice }: Props) => {
  const { deal } = tokenPrice;
  const { buyer, seller } = deal;

  return (
    <Wrapper>
      <Horizontal>
        <FontAwesomeIcon
          icon={faArrowCircleRight}
          color="var(--c-score-high)"
        />
        <LabelM>
          <FormattedMessage
            id="BoxOpeningOnboardingDialog.priceStep.boughtBy"
            defaultMessage="Bought by"
          />
        </LabelM>
      </Horizontal>
      {isType(buyer, 'User') && (
        <Horizontal>
          <Avatar size={2} rounded user={buyer} />
          <Nickname bold>{buyer.nickname}</Nickname>
        </Horizontal>
      )}
      <Horizontal>
        <FontAwesomeIcon icon={faArrowCircleLeft} color="var(--c-red-400)" />
        <LabelM>
          <FormattedMessage
            id="BoxOpeningOnboardingDialog.priceStep.soldBy"
            defaultMessage="Sold by"
          />
        </LabelM>
      </Horizontal>
      {isType(seller, 'User') ? (
        <Horizontal>
          <Avatar size={2} rounded user={seller} />
          <Nickname bold>{seller?.nickname}</Nickname>
        </Horizontal>
      ) : (
        <LabelM bold>Sorare</LabelM>
      )}
    </Wrapper>
  );
};

CardTransaction.fragments = {
  tokenPrice: gql`
    fragment CardTransaction_tokenPrice on TokenPrice {
      id
      deal {
        ... on TokenOperationTradeInterface {
          id
          buyer: userBuyer {
            slug
            nickname
            ...Avatar_user
          }
          seller: userSeller {
            slug
            nickname
            ...Avatar_user
          }
        }
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<CardTransaction_tokenPrice>,
};
